import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, XCircle, type LucideIcon } from "lucide-react";

import { useCraneT } from "../lib/i18n";
import { cn } from "../lib/utils";

type ToastKind = "success" | "error" | "warning";
type ToastItem = { id: number; kind: ToastKind; message: string };

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
};

const TOAST_CONFIG: Record<ToastKind, { Icon: LucideIcon; text: string; ring: string; labelKey: string }> = {
  success: {
    Icon: CheckCircle2,
    text: "text-crane-status-online",
    ring: "ring-crane-status-online/50",
    labelKey: "common.result",
  },
  error: {
    Icon: XCircle,
    text: "text-crane-status-alarm",
    ring: "ring-crane-status-alarm/50",
    labelKey: "common.error",
  },
  warning: {
    Icon: AlertTriangle,
    text: "text-crane-status-attention",
    ring: "ring-crane-status-attention/50",
    labelKey: "common.warning",
  },
};

const ToastContext = createContext<ToastApi | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const t = useCraneT();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const nextId = useRef(0);
  const timersByContent = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const push = useCallback((kind: ToastKind, message: string) => {
    const content = `${kind}:${message}`;
    const running = timersByContent.current.get(content);
    if (running) clearTimeout(running);

    const id = (nextId.current += 1);
    setToasts((prev) =>
      prev.some((toast) => toast.kind === kind && toast.message === message) ? prev : [...prev, { id, kind, message }],
    );

    timersByContent.current.set(
      content,
      setTimeout(() => {
        timersByContent.current.delete(content);
        setToasts((prev) => prev.filter((toast) => `${toast.kind}:${toast.message}` !== content));
      }, 4000),
    );
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      warning: (m) => push("warning", m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-crane-toast flex flex-col gap-2"
        aria-label={t("common.notifications")}
      >
        {toasts.map((toast) => {
          const { Icon, text, ring, labelKey } = TOAST_CONFIG[toast.kind];
          return (
            <div
              key={toast.id}
              role="status"
              aria-label={t(labelKey)}
              className={cn(
                "pointer-events-auto flex min-w-crane-toast max-w-crane-toast items-center gap-2 rounded-crane-sm bg-crane-card-elevated px-3.5 py-2.5 text-xs font-semibold shadow-crane-soft-lg ring-1",
                text,
                ring,
              )}
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              <span className="min-w-0">{toast.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast musi być użyte wewnątrz ToastProvider");
  return ctx;
};
